import type {
  ConfigEntityDetail,
  EntityDetailSchema,
} from '@web-config-app/core';
import type { GetEntityOnEnvironmentById } from '../../types/operation.types';
import { applyIdTransformations } from './apply-id-transformations';

export interface ImportIdTransformerResult {
  transformed: boolean;
  data: ConfigEntityDetail;
}

export const configEntityExists = async (
  configEntityId: string,
  getEntityById: GetEntityOnEnvironmentById,
): Promise<boolean> => {
  const result = await getEntityById(configEntityId);
  return result.status === 'success';
};

/**
 * Transforms **all** IDs needed for import by checking existence and remapping as needed.
 *
 * Flow:
 * 1. If the main entity exists in target environment (by config entity UUID), skip import
 * 2. If an entity with the same (domainEntityType + domainEntityId) exists in the target, skip import
 * 3. Check each relationship entity UUID for existence in target
 * 4. Apply transformations to:
 *    - root config entity UUID (`data.id`)
 *    - relationship UUIDs in JSON:API `relationships` and fields annotated with
 *      `x-entity-reference.relationship` (schema-linked config entity refs)
 *    - root domainEntityId (`attributes.entityMetadata.domainEntityId`)
 *    - linked domainEntityIds in fields annotated with
 *      `x-entity-reference.domainEntityType` (schema-linked domain refs)
 *    - excludes asset refs annotated with `x-entity-asset` (images/pdfs)
 */
export const transformIdsForImport = async (
  data: ConfigEntityDetail,
  getEntityById: GetEntityOnEnvironmentById,
  schema: EntityDetailSchema,
  tenantId: string,
  linkedEntityExists: (
    domainEntityType: string,
    domainEntityId: string,
  ) => Promise<boolean>,
): Promise<ImportIdTransformerResult> => {
  // Check if main entity already exists - if it exists, no transformation needed as it will be skipped during the import process
  if (await configEntityExists(data.id, getEntityById)) {
    return { transformed: false, data };
  }

  /**
   * Skip-duplicates behavior:
   * If an entity with the same (domainEntityType, domainEntityId) already exists in the target,
   * we skip import rather than rewriting the domainEntityId and creating a duplicate with a different ID.
   */
  const rootDomainEntityType =
    data.attributes?.entityMetadata?.domainEntityType;
  const rootDomainEntityId = data.attributes?.entityMetadata?.domainEntityId;
  if (
    typeof rootDomainEntityType === 'string' &&
    rootDomainEntityType.length > 0 &&
    typeof rootDomainEntityId === 'string' &&
    rootDomainEntityId.length > 0
  ) {
    const existsOnTarget = await linkedEntityExists(
      rootDomainEntityType,
      rootDomainEntityId,
    );
    if (existsOnTarget) {
      return { transformed: false, data };
    }
  }

  // Extract all relationship IDs from the relationships object
  const relationshipIds: string[] = [];
  if (data.relationships) {
    Object.values(data.relationships).forEach((relationship) => {
      if (!relationship.data) return;
      const entities = Array.isArray(relationship.data)
        ? relationship.data
        : [relationship.data];
      entities.forEach((entity) => {
        if (entity?.id) relationshipIds.push(entity.id);
      });
    });
  }

  // Check which relationship entities exist in target environment
  const relationshipExistenceChecks = await Promise.all(
    relationshipIds.map(async (configEntityId) => ({
      configEntityId,
      exists: await configEntityExists(configEntityId, getEntityById),
    })),
  );

  // Build set of IDs that need transformation (entities that don't exist in target)
  const relationshipIdsToTransform = new Set(
    relationshipExistenceChecks
      .filter(({ exists }) => !exists)
      .map(({ configEntityId }) => configEntityId),
  );

  // Apply all ID transformations
  const transformedEntity = await applyIdTransformations(
    data,
    relationshipIdsToTransform,
    schema,
    tenantId,
    linkedEntityExists,
  );

  return { transformed: true, data: transformedEntity };
};
